import { useState, useCallback } from 'react'
import { TagRecommendation } from '@/types'
import apiService from '@/services/api'

export interface ContentTagOptions {
  max_tags: number
  tag_types: ('keywords' | 'topics' | 'categories' | 'entities' | 'document_type')[]
}

export interface UseContentTagSuggestionsReturn {
  suggestions: TagRecommendation[]
  isLoading: boolean
  error: string | null
  options: ContentTagOptions
  setOptions: (options: Partial<ContentTagOptions>) => void
  fetchContentSuggestions: (fileId: number, existingTags: string[]) => Promise<void>
  clearError: () => void
}

export function useContentTagSuggestions(): UseContentTagSuggestionsReturn {
  const [suggestions, setSuggestions] = useState<TagRecommendation[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [options, setOptionsState] = useState<ContentTagOptions>({
    max_tags: 10,
    tag_types: ['keywords', 'topics', 'categories'],
  })

  const setOptions = useCallback((newOptions: Partial<ContentTagOptions>) => {
    setOptionsState((prev) => ({ ...prev, ...newOptions }))
  }, [])

  const fetchContentSuggestions = useCallback(
    async (fileId: number, existingTags: string[]) => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await apiService.generateContentTags(fileId, options)

        // Filter out existing tags
        const filteredSuggestions = response.suggestions.filter(
          (rec) => !existingTags.includes(rec.tag_name)
        )

        setSuggestions(filteredSuggestions)
      } catch (err: any) {
        setError(
          err.response?.data?.error || err.message || 'Failed to generate content-based tags'
        )
      } finally {
        setIsLoading(false)
      }
    },
    [options]
  )

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    suggestions,
    isLoading,
    error,
    options,
    setOptions,
    fetchContentSuggestions,
    clearError,
  }
}
